import { useRef, useState, useEffect } from 'react'
import { Input } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { debounce } from 'lodash'
import './SearchInput.scss'

const SearchInput = ({
  label,
  placeholder,
  name,
  size = 'md',
  setValue,
  onValuesChange, // Optional callback to get both value (typed) and committedValue (last search)
}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const inputRef = useRef(null)
  // value: current typed text (not yet committed to URL)
  const [value, setValueState] = useState('')
  // committedValue: last value pushed to URL (previously debouncedValue)
  const [committedValue, setCommittedValue] = useState('')

  // Sync value with query parameters on mount and location change
  useEffect(() => {
    // Keep input in sync if URL changes externally (navigation/back/forward)
    const searchParams = new URLSearchParams(history.location.search)
    const paramValue = searchParams.get(name || 'search') || ''
    setValueState(paramValue)
    setCommittedValue(paramValue)
    if (onValuesChange) {
      onValuesChange({ value: paramValue, committedValue: paramValue })
    }
  }, [history.location.search, name, onValuesChange])

  const updateQueryParams = (searchValue) => {
    const newSearchParams = new URLSearchParams(history.location.search)

    if (!searchValue) {
      newSearchParams.delete(name || 'search')
    } else {
      newSearchParams.set(name || 'search', searchValue)
      newSearchParams.set('page', 1)
    }

    history.push({
      pathname: history.location.pathname,
      search: newSearchParams.toString(),
    })
  }
  useEffect(() => {
    // Debounce commitSearch using lodash debounce
    if (setValue) return; // Only debounce if not controlled externally

    const debouncedCommit = debounce((val) => {
      if (val !== committedValue) {
        commitSearch(val);
      }
    }, 1200);

    debouncedCommit(value);

    return () => {
      debouncedCommit.cancel();
    };
  }, [value, committedValue, setValue]);
  // Commit the current typed value to URL (trigger search)
  const commitSearch = (searchValue) => {
    setCommittedValue(searchValue)
    if (onValuesChange) {
      onValuesChange({ value, committedValue: searchValue })
    }
    if (!setValue) {
      updateQueryParams(searchValue)
    }
    // Maintain focus without flicker
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  const handleInputChange = (e) => {
    const newValue = e.target.value
    setValueState(newValue)

    // Notify consumer about current typing (not yet committed)
    if (onValuesChange) {
      onValuesChange({ value: newValue, committedValue })
    }
    if (setValue) {
      setValue(newValue)
    }

    // NO automatic URL push here; wait for Enter or blur
  }

  const handlePressEnter = (e) => {
    const newValue = e.target.value
    if (setValue) return
    commitSearch(newValue)
  }

  const handleClear = () => {
    setValueState('')
    setCommittedValue('')
    if (onValuesChange) {
      onValuesChange({ value: '', committedValue: '' })
    }
    if (setValue) {
      setValue('')
    } else {
      updateQueryParams('')
    }
  }

  return (
    <div className={`search-input ${size}`}>
      {label && <label htmlFor={name}>{label}</label>}
      <Input
        allowClear
        ref={inputRef}
        autoComplete='off'
        id={name}
        name={name}
        value={value}
        placeholder={placeholder || `${t('nav.search.ph')}...`}
        suffix={<SearchOutlined className='text-dimmed' />}
        onChange={handleInputChange}
        onPressEnter={handlePressEnter}
        onBlur={() => {
          // Optionally commit on blur if value changed and not propagated yet
          if (!setValue && value !== committedValue) {
            commitSearch(value)
          }
        }}
        onClear={handleClear}
      />
    </div>
  )
}

export default SearchInput
